import React, { FC, PropsWithChildren, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
// material-ui
import {
  Box,
  Grid,
  Typography,
  SelectChangeEvent,
  Button,
  Avatar,
  Select,
  Checkbox,
  MenuItem,
  ListItemText,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import CustomTextField from "../FormFields/CustomTextField";
import {
  IAccountBasicFormInputs
} from "../../utils/account.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { StyledInput } from './Dialog.styles';
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import LoadingButton from "@mui/lab/LoadingButton";
import { ApiStatus } from "../../utils/api.types";
import { useAppSelector } from "../../store/hooks";

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  data?: any;
}

// Schema to validate account form.
const schema = yup.object().shape({
  name: yup.string().required("Name is required."),
  legalName: yup.string().required("Legal Name is required."),
  taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
  taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
});

//Custom Dialog Component.
const CustomDialogForm: FC<PropsWithChildren<IDialogProps>> = ({
  title,
  onClose,
  open,
  children,
  onSubmit,
  data,
}) => {
  const [industryVerticalOptions, setIndustryVerticalOptions] = useState<IOptions[]>([]);
  const [selectedIndustryVertical, setSelectedIndustryVertical] = useState('');
  // const [selectedCompanyType, setSelectedCompanyType] = useState("");
  //const [companyTypeOptions, setCompanyTypeOptions] = useState<IOptions[]>([]);
  const { status } = useAppSelector((state) => state.account);
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [signImage, setSignImage] = useState("");
  const [selectedSignFile, setSelectedSignFile] = useState<File>();
  
  const [selectedBusinessType, setSelectedBusinessType] = React.useState<string[]>([]);

  const [checked, setChecked] = useState(true);
  const [logoChecked, setLogoChecked] = useState(true);

  const businessTypeOptions = [
    'Product',
    'Service',
    'Consulting'
  ];

  // Populate Industry Vertical on Account form load
  useEffect(() => {
    let options = [
      {
        label: "Aerospace (aircraft manufacturing)",
        value: "Aerospace (aircraft manufacturing)",
      },
      { label: "Agriculture", value: "Agriculture" },
      { label: "Chemical (manufacturing)", value: "Chemical (manufacturing)" },
      { label: "Computers", value: "Computers" },
      { label: "Construction", value: "Construction" },
      { label: "Defense", value: "Defense" },
      {
        label: "Energy (production, distribution)",
        value: "Energy (production, distribution)",
      },
      { label: "Entertainment", value: "Entertainment" },
      { label: "Financial", value: "Financial" },
      { label: "Food", value: "Food" },
      { label: "Health Care", value: "Health Care" },
      { label: "Hospitality", value: "Hospitality" },
      { label: "Information", value: "Information" },
      { label: "Manufacturing", value: "Manufacturing" },
      { label: "Mass Media", value: "Mass Media" },
      { label: "Telecommunications", value: "Telecommunications" },
      { label: "Transport", value: "Transport" },
      { label: "Water", value: "Water" },
    ];
    setIndustryVerticalOptions(options);
  }, []);

  // Populate Company Type on Account form load.
  // useEffect(() => {
  //   let options = [
  //     { label: "Proprietorship", value: "Proprietorship" },
  //     { label: "Partnership", value: "Partnership" },
  //     { label: "Limited Liability Partnership", value: "Limited Liability Partnership" },
  //     { label: "Private Limited Companies", value: "Private Limited Companies" },
  //     { label: "Public Limited Companies", value: "Public Limited Companies" },
  //     { label: "One Person Company", value: "One Person Company" },
  //     { label: "Non Profit Company", value: "Non Profit Company" }
  //   ]
  //   setCompanyTypeOptions(options);
  // }, []);

  // use form
  const methods = useForm<IAccountBasicFormInputs>({
    resolver: yupResolver(schema)
  });

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;
    console.log(target.files);

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };

  const handleSignSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;
    console.log(target.files);

    if (target.files && target.files[0]) {
      setSelectedSignFile(target.files[0]);
      setSignImage(URL.createObjectURL(target.files[0]));
    }
  };

  const innerSubmit = async (data: any) => {
    const form = new FormData();
    form.append('name', data.name);
    form.append('legalName', data.legalName);
    form.append('industryVertical', data.industryVertical);
    form.append('displayName', data.displayName);
    form.append('companyDesc', data.companyDesc);
    form.append('businessType', JSON.stringify(selectedBusinessType));
    form.append('taxIdentificationNumber1', data.taxIdentificationNumber1);
    form.append('taxIdentificationNumber2', data.taxIdentificationNumber2);
    form.append('website', data.website);
    form.append('contactName', data.contactName);
    form.append('email', data.email);
    form.append('whatsAppNumber', data.whatsAppNumber);

    if(checked == true) {
      form.append('showSignatureOnInvoice', 'true');
    } else {
      form.append('showSignatureOnInvoice', 'false');
    }

    if(logoChecked == true) {
      form.append('showLogoOnInvoice', 'true');
    } else {
      form.append('showLogoOnInvoice', 'false');
    }
    
    if (selectedFile) {
      form.append('logo', selectedFile);
    }
    if(selectedSignFile){
      form.append('signature', selectedSignFile);
    }
    //onSubmit({...data, ...{subscription: subscriptionPlan?.value, logo: form }});
    onSubmit(form);
  };

  const handleSelectChange = (
    event: SelectChangeEvent<typeof selectedBusinessType>
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedBusinessType(
      typeof value === "string" ? value.split(",") : value
    );
  };

  useEffect(() => {
    let basicInfoAvaliable = true;

    const setInitialData = () => {
      try {
        if (data && basicInfoAvaliable) {
          methods.setValue('name', data.name);
          methods.setValue('displayName', data.displayName);
          methods.setValue('legalName', data.legalName);
          methods.setValue('companyDesc', data.companyDesc);
          methods.setValue('website', data.website);
          methods.setValue('taxIdentificationNumber1', data.taxIdentificationNumber1);
          methods.setValue('taxIdentificationNumber2', data.taxIdentificationNumber2);
          methods.setValue('contactName', data.contactName);
          methods.setValue('email', data.email);
          methods.setValue('whatsAppNumber', data.whatsAppNumber);
          if (data.businessType) setSelectedBusinessType(data.businessType);
          setSelectedIndustryVertical(data.industryVertical);
          //setSelectedCompanyType(data.companyType);
          setImage(data.logo);
          setSignImage(data.signature);
          setLogoChecked(data.showLogoOnInvoice);
          setChecked(data.showSignatureOnInvoice);
        }
      } catch (error: any) {
        console.log("Dialog catch block called");
      }
    };

    setInitialData();

    return () => {
      basicInfoAvaliable = false;
    };
  }, [data, methods]);

  return (
    <div className="drawer-form-container">
      <FormProvider {...methods}>
        <Box
          sx={{ display: "flex", flexDirection: "column", height: "100vh", padding: "16px 0 16px 16px" }}
          component="form"
          onSubmit={methods.handleSubmit(innerSubmit)}
          noValidate
        >
          <DialogTitle sx={{ height: "0px", mt: 1, mb: 1, justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h5">{title}</Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Grid container justifyContent="left">
              <Grid item xs={12} md={12} sm={12}>
                <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyItems: "center", alignItems: "center" }}>
                  <div className="file-name" style={{ marginBottom: "10px" }}>
                    {image && (
                      <Avatar
                        alt={"Picture"}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                        src={image}
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "contain",
                        }}
                        variant='square'
                        sizes={"lg"}
                      />
                    )}
                  </div>
                  <label htmlFor="logo">
                    <StyledInput
                      accept="image/*"
                      id="logo"
                      type="file"
                      onChange={handleSelectFile}
                    />
                    <Button
                      variant="outlined"
                      className="btn-choose"
                      component="span"
                    >
                      Choose Logo
                    </Button>
                  </label>
                  
                  <div className="file-name" style={{ marginBottom: "10px" }}>
                    {signImage && (
                      <Avatar
                        alt={"Picture"}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                        src={signImage}
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "contain",
                        }}
                        variant='square'
                        sizes={"lg"}
                      />
                    )}
                  </div>
                  <label htmlFor="signature">
                    <StyledInput
                      accept="image/*"
                      id="signature"
                      type="file"
                      onChange={handleSignSelectFile}
                    />
                    <Button
                      variant="outlined"
                      className="btn-choose"
                      component="span"
                    >
                      Choose Signature
                    </Button>
                  </label>

                  {(<FormControlLabel
                    control={
                      <Checkbox
                        checked={logoChecked}
                        onChange={(event) => {
                          setLogoChecked(event.target.checked);
                        }}
                        name="checked"
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="h6">Show Logo On Invoice</Typography>}
                  />)}
                  {(<FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(event) => {
                          setChecked(event.target.checked);
                        }}
                        name="checked"
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="h6">Show Signature On Invoice</Typography>}
                  />)}
                </div>
                <div>
                  <CustomTextField
                    label="Brand Name"
                    name="name"
                    required
                    variant="standard"
                  />
                  <CustomTextField
                    label="Display Name"
                    name="displayName"
                    variant="standard"
                  />
                  <CustomTextField
                    label="Legal Name"
                    name="legalName"
                    required
                    variant="standard"
                  />
                  <CustomTextField
                    label="Company Description"
                    name="companyDesc"
                    variant="standard"
                    multiline
                    rows={2}
                  />
                  {/* <CustomSelect
                    name="companyType"
                    label="Company Type"
                    id="companyType"
                    variant="standard"
                    options={companyTypeOptions}
                    value={selectedCompanyType || ""}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setSelectedCompanyType(event.target.value);
                    }}
                  /> */}
                  <CustomTextField
                    label="Website"
                    name="website"
                    variant="standard"
                  />
                  <CustomTextField label="Tax Identification #1 (PAN for India)" required name="taxIdentificationNumber1" variant="standard" />
                  <CustomTextField label="Tax Identification #2 (GST for India)" required name="taxIdentificationNumber2" variant="standard" />
                  <CustomSelect
                    name="industryVertical"
                    label="Industry Vertical"
                    id="industryVertical"
                    variant="standard"
                    options={industryVerticalOptions}
                    value={selectedIndustryVertical}
                    onChange={(event: SelectChangeEvent<any>) => {
                      setSelectedIndustryVertical(event.target.value);
                    }}
                  />
                  <FormControl
                    fullWidth
                    sx={{ mt: 2, mb: 4 }}
                    variant="standard"
                  >
                    <InputLabel id={"multiple-checkbox-label"}>
                      Business Type
                    </InputLabel>
                    <Select
                      labelId="multiple-checkbox-label"
                      id="businessType"
                      multiple
                      value={selectedBusinessType}
                      onChange={(event: SelectChangeEvent<any>) => {
                        handleSelectChange(event);
                      }}
                      renderValue={(selected: any) => selected.join(", ")}
                    >
                      {businessTypeOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                          <Checkbox
                            checked={
                              selectedBusinessType.indexOf(name) > -1
                            }
                          />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <CustomTextField label="Contact Name" name="contactName" variant="standard" />
                  <CustomTextField label="Email" name="email" variant="standard" />
                  <CustomTextField label="WhatsApp Number" name="whatsAppNumber" variant="standard" />

                </div>
              </Grid>
            </Grid>
            {children}
          </DialogContent>

          <DialogActions sx={{ height: "40px", mb: 2 }}>
            <Grid container gap='0' sx={{ mr: '32px', pt: '10px' }}>
              <Grid item xs={12} md={12} sm={12} lg={12} display="flex" justifyContent="flex-end" >
                <Button variant='outlined'
                  style={{ marginRight: '10px' }}
                  onClick={() => {
                    methods.reset();
                    onClose();
                  }}>Cancel</Button>
                <LoadingButton
                  type="submit"
                  loading={status === ApiStatus.LOADING}
                  variant="contained"
                  color="secondary"
                >
                  {'Update'}
                </LoadingButton>
              </Grid>
            </Grid>
          </DialogActions>
        </Box>
      </FormProvider>
    </div>
  );
};

export default CustomDialogForm;
